import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { loadExcelSettings } from "./excelSettings";
import { COLUMN_LETTERS, isSectionRow, normalizeHeader } from "./excelStructure";

// =====================================================
// EXCEL DATE HANDLING UTILITIES
// =====================================================
/**
 * Helper function to detect if a value is an Excel date number
 * @param {any} value - The value to check
 * @returns {boolean} - True if it's likely an Excel date
 */
const isExcelDate = (value) => {
  // Check if it's a number that could be an Excel date (between reasonable date ranges)
  // Excel dates start from 1900-01-01 (value 1) to far future
  return typeof value === "number" && value > 1 && value < 100000;
};

/**
 * Convert Excel serial date to formatted string
 * @param {number} serialDate - Excel serial date number
 * @param {string} format - Desired format (default: "mmm-yy")
 * @returns {string} - Formatted date string
 */
const formatExcelDate = (serialDate, format = "mmm-yy") => {
  try {
    // Excel dates start from 1900-01-01, but JavaScript dates start from 1970-01-01
    // Excel serial date 1 = January 1, 1900
    // But Excel incorrectly treats 1900 as a leap year, so we need to adjust
    const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
    const jsDate = new Date(excelEpoch.getTime() + serialDate * 24 * 60 * 60 * 1000);

    if (format === "mmm-yy") {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[jsDate.getMonth()];
      const year = jsDate.getFullYear().toString().slice(-2); // Get last 2 digits
      return `${month}-${year}`;
    }

    // Default fallback
    return jsDate.toLocaleDateString();
  } catch (error) {
    console.warn(`Failed to format Excel date ${serialDate}:`, error);
    return String(serialDate); // Fallback to original value
  }
};

// =====================================================
// EXCEL COLUMN CONFIGURATION - Easy to modify
// =====================================================
// Column widths for the final downloaded Excel sheet (in Excel width units)
// Note: Excel width units are approximately: 1 unit ≈ 7-8 pixels
// Get column widths from settings - with fallbacks for safety
const EXCEL_COLUMN_WIDTHS = (() => {
  const settings = loadExcelSettings();
  const widths =
    settings && settings.columnWidths
      ? settings.columnWidths
      : {
          A: 10,
          B: 18,
          C: 45,
          D: 55,
          E: 20,
          F: 15,
          G: 15,
          H: 10,
          I: 15,
          J: 20,
          K: 20,
          L: 20,
        };

  return {
    ITEM_NO: widths.A || 10,
    SHORT_CODE: widths.B || 18,
    CLASS_NAME: widths.C || 45,
    UNIT_NAME: widths.D || 55,
    TUTOR_NAME: widths.E || 20,
    DAY_TIME: widths.F || 15,
    EW_TIME: widths.G || 15,
    GROUP: widths.H || 10,
    COHORT: widths.I || 15,
    ROOM_ALLOCATION: widths.J || 20,
    STUDENTS_COMING: widths.K || 20,
    STUDENT_NUMBER: widths.L || 20,
  };
})();

// Column mapping based on Excel macro analysis
const COLUMN_MAPPING = {
  A: 0,
  B: 1,
  C: 2,
  D: 3,
  E: 4,
  F: 5,
  G: 6,
  H: 7,
  I: 8,
  J: 9,
  K: 10,
  L: 11,
  M: 12,
  N: 13,
  O: 14,
  P: 15,
  Q: 16,
  R: 17,
  S: 18,
  T: 19,
  U: 20,
  V: 21,
  W: 22,
  X: 23,
  Y: 24,
  Z: 25,
  AA: 26,
  AB: 27,
};

// Expected column structure for validation (configurable header row)
// Always fetch from current Settings so runtime changes are respected
const getExpectedColumns = () => {
  const settings = loadExcelSettings();
  return (
    (settings && settings.columnHeaders) || {
      A: "Item No.",
      B: "Unit No.",
      C: "VLE Class Name",
      D: "Unit Name",
      E: "Lecturer",
      F: "Day/Time",
      G: "EW/Time",
      H: "Group",
      I: "Cohort",
      J: "Room Allocated",
      K: "Students Coming",
      L: "Student Number",
    }
  );
};

// Validation function to check if Excel file has correct structure
const validateExcelStructure = (rawData) => {
  const errors = [];

  // Check if file has minimum required rows
  if (!rawData || rawData.length < 5) {
    errors.push("Excel file must have at least 5 rows of data");
  }

  // Check if file has minimum required columns (at least up to column L = index 11)
  const firstDataRow = rawData.find((row) => row && row.length > 10) || [];
  if (firstDataRow.length < 12) {
    errors.push("Excel file must have columns up to at least column L (12 columns minimum)");
  }

  // Get Excel settings
  const settings = loadExcelSettings();
  console.log("🔧 Validation using Settings:", settings);

  // Check for correct column headers in the configured header row
  const headerRowIndex = settings.headerRow ? settings.headerRow - 1 : 2; // Default to Row 3 (index 2)
  console.log(
    `🔧 Will validate headers in row ${settings.headerRow || 3} (index ${headerRowIndex})`
  );

  if (rawData.length > headerRowIndex) {
    const headerRow = rawData[headerRowIndex] || [];
    console.log(`🔧 Found in row ${settings.headerRow || 3}:`, headerRow.slice(0, 5)); // First 5 columns
    console.log(`🔧 Expected first header: "${getExpectedColumns().A}"`);
    console.log(`🔧 Actual first header: "${String(headerRow[0] || "").trim()}"`);

    const expectedColumns = getExpectedColumns();
    let mismatchedHeaders = [];

    // Every column header in the file must match the one configured in Settings
    COLUMN_LETTERS.forEach((colLetter) => {
      const expectedHeader = String(expectedColumns[colLetter] || "");
      const actualHeader = String(headerRow[COLUMN_MAPPING[colLetter]] || "").trim();

      if (normalizeHeader(actualHeader) !== normalizeHeader(expectedHeader)) {
        mismatchedHeaders.push(
          `Column ${colLetter}: Settings expects "${expectedHeader}", Excel has ${
            actualHeader ? `"${actualHeader}"` : "(empty)"
          }`
        );
      }
    });

    if (mismatchedHeaders.length > 0) {
      errors.push(
        `Column headers in Row ${
          settings.headerRow || 3
        } don't match your Settings (${mismatchedHeaders.length} of ${
          COLUMN_LETTERS.length
        } columns):\n${mismatchedHeaders.join(
          "\n"
        )}\n\nFix the header text in the Excel file, or update Settings > Column Headers so it matches.`
      );
    }
  }

  // Look for actual data rows (rows that have content in key columns after configured start row)
  let dataRowsFound = 0;
  let sampleDataRow = null;
  const startRowIndex = settings && settings.startRow ? settings.startRow - 1 : headerRowIndex + 1;
  for (let i = startRowIndex; i < Math.min(rawData.length, startRowIndex + 50); i++) {
    // Check rows after header (using configurable header and start rows from settings)
    const row = rawData[i] || [];
    // Check if row has data in key columns (A, E, F, K)
    if (row[0] || row[4] || row[5] || row[10]) {
      // A, E, F, K columns
      dataRowsFound++;
      if (!sampleDataRow) sampleDataRow = row;
    }
  }

  if (dataRowsFound < 1) {
    errors.push(
      "No valid data rows found. Please check that your Excel file contains room allocation data in the expected format with data in columns A (Item No), E (Tutor Name), F (Day/Time), and K (Students Coming)"
    );
  }

  // Additional validation: Check if this looks like a room allocation file
  if (sampleDataRow) {
    const hasAnyExpectedPatterns =
      String(sampleDataRow[5] || "").match(/(mon|tue|wed|thu|fri|sat)/i) || // Day patterns in column F
      String(sampleDataRow[6] || "").match(/(am|pm|eve)/i) || // Time patterns in column G
      !isNaN(Number(sampleDataRow[10])); // Numeric values in column K (students)

    if (!hasAnyExpectedPatterns) {
      errors.push(
        "This doesn't appear to be a room allocation Excel file. Expected to find day names (Mon-Sat) in column F (Day/Time), time periods (AM/PM/Eve) in column G (EW/Time), or student numbers in column K (Students Coming)"
      );
    }
  }

  return errors;
};

/**
 * Read a workbook into plain rows without judging its structure.
 * The format detector needs the rows even when validation would reject them.
 * @returns {Promise<{rows: Array<Array>, sheetName: string}>}
 */
export const readWorkbookRows = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Look for RoomAllocation sheet or use first sheet
        let sheetName = "RoomAllocation";
        if (!workbook.Sheets[sheetName]) {
          sheetName = workbook.SheetNames[0];
        }

        if (!workbook.Sheets[sheetName]) {
          throw new Error("No valid worksheet found in the Excel file");
        }

        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          defval: "",
          raw: true, // CRITICAL: Preserve exact cell values including leading/trailing spaces
        });

        resolve({ rows, sheetName });
      } catch (error) {
        reject(new Error(`Failed to read Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

export const parseExcelFile = (file) => {
  return readWorkbookRows(file).then((workbook) => {
    const jsonData = workbook.rows;

    // Validate Excel structure before processing
    const validationErrors = validateExcelStructure(jsonData);
    if (validationErrors.length > 0) {
      const settings = loadExcelSettings();

      // Build the expected header labels dynamically from Settings
      const ch = (settings && settings.columnHeaders) || getExpectedColumns();
      const orderedCols = COLUMN_LETTERS.map((letter) => ch[letter]);

      const errorMessage = `Invalid Excel file format:\n\n${validationErrors.join(
        "\n\n"
      )}\n\nPlease check your Settings and ensure your Excel file follows them:\n\u2022 Row ${
        settings.headerRow || 3
      } should contain column headers: ${orderedCols.join(
        ", "
      )}\n\u2022 Data should start from Row ${settings.startRow || 4} onwards`;

      throw new Error(errorMessage);
    }

    return parseWorksheetData(jsonData);
  });
};

const parseWorksheetData = (rawData) => {
  if (!rawData || rawData.length < 4) {
    throw new Error(
      "Excel file appears to be empty or has insufficient data. Please ensure your file contains at least 4 rows with proper room allocation data."
    );
  }

  // Extract header information from specific cells and preserve original Row 1 structure
  // Capture the original Row 1 for preservation in export
  const originalRow1 = rawData[0] || [];

  const headerInfo = {
    // Get value from A1 (row index 0, column 0) - this is the main caption
    session: rawData[0] && rawData[0][0] ? rawData[0][0] : "",
    // Don't extract department from Row 2 - only use Row 1 content for display
    department: "",
    // Preserve complete Row 1 for export structure consistency
    originalRow1: originalRow1,
  };

  // If session contains both session and department info in Row 1, split them
  if (headerInfo.session && headerInfo.session.toLowerCase().includes("department")) {
    const parts = headerInfo.session.split(/department\s*:\s*/i);
    if (parts.length === 2) {
      headerInfo.session = parts[0].trim();
      headerInfo.department = `Department : ${parts[1].trim()}`;
    }
  }

  // The configured column headers, in sheet order (A..L) - labels the on-screen table
  const configuredColumns = getExpectedColumns();
  const expectedHeaders = COLUMN_LETTERS.map((letter) => configuredColumns[letter] || "");

  const data = [];

  // Get Excel settings
  const settings = loadExcelSettings();
  const headerRowIndex = settings.headerRow ? settings.headerRow - 1 : 2; // Default to Row 3 (index 2)
  const startRowIndex = settings.startRow - 1;

  // First, check for any section headers that might appear between header row and data start row
  for (let i = headerRowIndex + 1; i < startRowIndex; i++) {
    const row = rawData[i];
    if (!row || row.length === 0) continue;

    const firstCell = String(row[0] || "");

    if (isSectionRow(row)) {
      // Add this section header to the data
      data.push({
        row_index: i + 1,
        is_section_header: true,
        section_title: firstCell,
        item_no: "",
        short_code: "",
        class_name: "",
        unit_name: "",
        tutor_name: "",
        day_time: "",
        ew_time: "",
        group: "",
        cohort: "",
        room_allocation: "",
        students_coming: "",
        student_number: "",
      });
    }
  }

  // Process data starting from the configured starting row
  for (let i = startRowIndex; i < rawData.length; i++) {
    const row = rawData[i];
    if (!row || row.length === 0) continue;

    // Skip the configured header row by position (professional approach)
    // This ensures we don't accidentally skip data rows that might contain similar text
    if (i === headerRowIndex) {
      continue; // Skip the exact configured header row
    }

    const firstCell = String(row[0] || "");

    if (isSectionRow(row)) {
      // This is a section header (like "September 2023 MSc - Sem 3")
      data.push({
        row_index: i + 1,
        is_section_header: true,
        section_title: firstCell,
        item_no: "",
        short_code: "",
        class_name: "",
        unit_name: "",
        tutor_name: "",
        day_time: "",
        ew_time: "",
        group: "",
        cohort: "",
        room_allocation: "",
        students_coming: "",
        student_number: "",
      });
    } else {
      // Regular data row - use new column structure
      const rowData = {
        row_index: i + 1,
        is_section_header: false,
        section_title: "",
        item_no: String(row[COLUMN_MAPPING.A] || ""), // A - Item No
        short_code: String(row[COLUMN_MAPPING.B] || ""), // B - Short Code
        class_name: String(row[COLUMN_MAPPING.C] || ""), // C - Class Name
        unit_name: String(row[COLUMN_MAPPING.D] || ""), // D - Unit Name
        tutor_name: String(row[COLUMN_MAPPING.E] || ""), // E - Tutor Name (PRESERVE EXACT SPACING)
        day_time: String(row[COLUMN_MAPPING.F] || ""), // F - Day/Time
        ew_time: String(row[COLUMN_MAPPING.G] || ""), // G - EW/Time
        group: String(row[COLUMN_MAPPING.H] || ""), // H - Group
        cohort: (() => {
          // I - Cohort (SPECIAL HANDLING FOR DATES)
          const cohortValue = row[COLUMN_MAPPING.I];
          if (isExcelDate(cohortValue)) {
            console.log(`🗓️ Converting Excel date ${cohortValue} to formatted string`);
            return formatExcelDate(cohortValue, "mmm-yy");
          }
          return String(cohortValue || "");
        })(),
        room_allocation: String(row[COLUMN_MAPPING.J] || ""), // J - Room Allocation
        students_coming: String(row[COLUMN_MAPPING.K] || ""), // K - Students Coming
        student_number: String(row[COLUMN_MAPPING.L] || ""), // L - Student Number

        // Keep legacy field names for backward compatibility with existing code
        unit_no: String(row[COLUMN_MAPPING.B] || ""), // B - Short Code (mapped to unit_no)
        vle_class_name: String(row[COLUMN_MAPPING.C] || ""), // C - Class Name (mapped to vle_class_name)
        intake: (() => {
          // I - Cohort (mapped to intake) - SPECIAL HANDLING FOR DATES
          const cohortValue = row[COLUMN_MAPPING.I];
          if (isExcelDate(cohortValue)) {
            return formatExcelDate(cohortValue, "mmm-yy");
          }
          return String(cohortValue || "");
        })(),
        room_auto_allo: String(row[COLUMN_MAPPING.J] || ""), // J - Room Allocation (mapped to room_auto_allo)
        student_numbers: String(row[COLUMN_MAPPING.L] || ""), // L - Student Number (mapped to student_numbers)
      };

      data.push(rowData);
    }
  }

  return {
    data,
    headerInfo,
    expectedHeaders,
  };
};

// Function to export teacher conflicts as a separate analysis
export const generateExcelFile = async (
  data,
  conflicts = [],
  unallocatedRooms = [],
  originalFileName = "room_allocation.xlsx",
  headerInfo = {}
) => {
  try {
    // Create a new workbook using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("RoomAllocation");

    // Define column headers and set up columns using Settings (fall back to defaults)
    const settings = loadExcelSettings();
    const columnHeaders = (settings && settings.columnHeaders) || {
      A: "Item No.",
      B: "Unit No.",
      C: "VLE Class Name",
      D: "Unit Name",
      E: "Lecturer",
      F: "Day/Time",
      G: "EW/Time",
      H: "Group",
      I: "Cohort",
      J: "Room Allocated",
      K: "Students Coming",
      L: "Student Number",
    };

    // Build headers array to match configured labels exactly, while keeping data keys fixed
    const headers = [
      { header: columnHeaders.A || "Item No.", key: "item_no", width: EXCEL_COLUMN_WIDTHS.ITEM_NO },
      {
        header: columnHeaders.B || "Unit No.",
        key: "short_code",
        width: EXCEL_COLUMN_WIDTHS.SHORT_CODE,
      },
      {
        header: columnHeaders.C || "VLE Class Name",
        key: "class_name",
        width: EXCEL_COLUMN_WIDTHS.CLASS_NAME,
      },
      {
        header: columnHeaders.D || "Unit Name",
        key: "unit_name",
        width: EXCEL_COLUMN_WIDTHS.UNIT_NAME,
      },
      {
        header: columnHeaders.E || "Lecturer",
        key: "tutor_name",
        width: EXCEL_COLUMN_WIDTHS.TUTOR_NAME,
      },
      {
        header: columnHeaders.F || "Day/Time",
        key: "day_time",
        width: EXCEL_COLUMN_WIDTHS.DAY_TIME,
      },
      { header: columnHeaders.G || "EW/Time", key: "ew_time", width: EXCEL_COLUMN_WIDTHS.EW_TIME },
      { header: columnHeaders.H || "Group", key: "group", width: EXCEL_COLUMN_WIDTHS.GROUP },
      { header: columnHeaders.I || "Cohort", key: "cohort", width: EXCEL_COLUMN_WIDTHS.COHORT },
      {
        header: columnHeaders.J || "Room Allocated",
        key: "room_allocation",
        width: EXCEL_COLUMN_WIDTHS.ROOM_ALLOCATION,
      },
      {
        header: columnHeaders.K || "Students Coming",
        key: "students_coming",
        width: EXCEL_COLUMN_WIDTHS.STUDENTS_COMING,
      },
      {
        header: columnHeaders.L || "Student Number",
        key: "student_number",
        width: EXCEL_COLUMN_WIDTHS.STUDENT_NUMBER,
      },
    ];

    // Don't use worksheet.columns = headers as it always puts headers in Row 1
    // Instead, manually set column widths and place headers in the configured row

    // Set column widths without headers
    for (let i = 0; i < headers.length; i++) {
      const col = worksheet.getColumn(i + 1);
      col.width = headers[i].width;
    }

    // Use the user's Settings to determine header row position
    const headerRowNumber = settings && settings.headerRow ? Number(settings.headerRow) : 1;

    // If original file had content in Row 1 and headers are not in Row 1, preserve Row 1
    if (headerInfo.originalRow1 && headerInfo.originalRow1.length > 0 && headerRowNumber > 1) {
      const originalRow1 = worksheet.getRow(1);
      for (let i = 0; i < headerInfo.originalRow1.length && i < 12; i++) {
        const cellValue = headerInfo.originalRow1[i];
        if (cellValue) {
          originalRow1.getCell(i + 1).value = cellValue;
        }
      }

      // Format Row 1: Merge cells A1 to L1 (columns 1 to 12)
      worksheet.mergeCells(1, 1, 1, headers.length);

      // Style Row 1: Bold, Blue font, Center alignment
      const row1Cell = worksheet.getRow(1).getCell(1);
      row1Cell.font = {
        bold: true,
        size: 16,
        color: { argb: "FF0066CC" }, // Blue color
      };
      row1Cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      row1Cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF0F8FF" }, // Light blue background
      };

      // Add border to the merged Row 1
      for (let col = 1; col <= headers.length; col++) {
        const cell = worksheet.getRow(1).getCell(col);
        cell.border = {
          top: { style: "medium", color: { argb: "FF0066CC" } },
          left: { style: "medium", color: { argb: "FF0066CC" } },
          bottom: { style: "medium", color: { argb: "FF0066CC" } },
          right: { style: "medium", color: { argb: "FF0066CC" } },
        };
      }
    }

    // Write headers to the configured row
    const headerRow = worksheet.getRow(headerRowNumber);
    for (let i = 0; i < headers.length; i++) {
      headerRow.getCell(i + 1).value = headers[i].header;
    } // Style the header row - apply to all defined header columns
    for (let col = 1; col <= headers.length; col++) {
      const headerCell = worksheet.getRow(headerRowNumber).getCell(col);
      headerCell.font = { bold: true, size: 14 };
      headerCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
      // Center align only columns J, K, L (columns 10, 11, 12)
      const alignment = col >= 10 && col <= 12 ? "center" : "left";
      headerCell.alignment = { horizontal: alignment, vertical: "middle" };
      headerCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    }

    // Define colors for different sections
    const sectionColors = [
      "FFE6E6FF", // Light Blue
      "FFE6F3FF", // Light Pink
      "FFE6FFE6", // Light Green
      "FFFFFF99", // Light Yellow
      "FFFFE6CC", // Light Orange
      "FFE6E6E6", // Light Gray
      "FFCCFFCC", // Light Mint
      "FFFFCCFF", // Light Purple
      "FFCCFFFF", // Light Cyan
      "FFFFCCCC", // Light Coral
    ];

    let currentSectionIndex = -1;
    // Start writing data AFTER the header row (headers are at headerRowNumber)
    let currentRow = headerRowNumber + 1;

    console.log(`Export: Header row will be at ${headerRowNumber}, data starts at ${currentRow}`);
    console.log(`Settings: headerRow=${settings?.headerRow}, startRow=${settings?.startRow}`);
    console.log(`First data row will be written to Excel row ${currentRow}`);

    // Helper function to create a delay
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Process data in batches with delays to prevent memory/performance issues
    const BATCH_SIZE = 50; // Process 50 rows at a time
    const DELAY_MS = 400; // 400ms delay between batches

    console.log(
      `Processing ${data.length} rows in batches of ${BATCH_SIZE} with ${DELAY_MS}ms delays...`
    );

    // Define row processing function outside the loop
    const processRow = (row) => {
      console.log(
        `📝 Processing row ${currentRow}: ${row.is_section_header ? "SECTION" : "DATA"} - "${
          row.is_section_header ? row.section_title : row.item_no
        }"`
      );

      if (row.is_section_header) {
        // Section header row
        currentSectionIndex = (currentSectionIndex + 1) % sectionColors.length;

        const sectionRow = worksheet.getRow(currentRow);
        sectionRow.getCell(1).value = row.section_title;

        // Merge cells across all columns for section header
        worksheet.mergeCells(currentRow, 1, currentRow, headers.length);

        // Style section header - apply to columns A through L only
        for (let col = 1; col <= headers.length; col++) {
          const cell = sectionRow.getCell(col);
          cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
          cell.alignment = { horizontal: "left", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }

        currentRow++;
      } else {
        // Regular data row - with proper data type handling
        const dataRow = worksheet.getRow(currentRow);

        // COMPLETELY ROBUST parseValue function - preserves ALL data
        const parseValue = (value, shouldBeNumber = false) => {
          // NEVER return empty string unless the original value was truly empty
          if (value === null || value === undefined) {
            return "";
          }

          // Convert to string first to handle all cases
          const stringValue = String(value);

          // If it's already empty string, return it
          if (stringValue === "" || stringValue === "null" || stringValue === "undefined") {
            return "";
          }

          if (shouldBeNumber) {
            // For room numbers and numeric fields, try multiple approaches
            const trimmed = stringValue.trim();

            // If it looks like a number, parse it
            if (/^\d+\.?\d*$/.test(trimmed) || /^\d*\.?\d+$/.test(trimmed)) {
              const parsed = Number(trimmed);
              if (!isNaN(parsed) && isFinite(parsed)) {
                return parsed;
              }
            }

            // If it contains only digits (like "402"), return as number
            if (/^\d+$/.test(trimmed)) {
              return parseInt(trimmed, 10);
            }

            // If parsing fails but we have a non-empty string, return it anyway
            // This ensures we NEVER lose data even if it's not a perfect number
            return trimmed;
          }

          return stringValue;
        };

        // Set values with proper data types - updated for new column structure
        dataRow.getCell(1).value = parseValue(row.item_no, true); // Item No - Number
        dataRow.getCell(2).value = parseValue(row.short_code || row.unit_no); // Short Code - Text
        dataRow.getCell(3).value = parseValue(row.class_name || row.vle_class_name); // Class Name - Text
        dataRow.getCell(4).value = parseValue(row.unit_name); // Unit Name - Text
        // CRITICAL: Preserve exact spacing for tutor names - DO NOT TRIM
        dataRow.getCell(5).value = String(row.tutor_name || ""); // Tutor Name - Preserve exact spacing
        dataRow.getCell(6).value = parseValue(row.day_time); // Day Time - Text
        dataRow.getCell(7).value = parseValue(row.ew_time); // EW Time - Text
        dataRow.getCell(8).value = parseValue(row.group); // Group - Text
        dataRow.getCell(9).value = parseValue(row.cohort || row.intake); // Cohort - Text

        // CRITICAL: Room allocation with extensive logging
        const originalRoomValue = row.room_allocation || row.room_auto_allo;
        const processedRoomValue = parseValue(originalRoomValue, true);

        // Log every single room assignment
        if (
          originalRoomValue !== null &&
          originalRoomValue !== undefined &&
          originalRoomValue !== ""
        ) {
          dataRow.getCell(10).value = processedRoomValue; // Room - PRESERVED VALUE
        }
        dataRow.getCell(11).value = parseValue(row.students_coming, true); // Students Coming - Number
        dataRow.getCell(12).value = parseValue(row.student_number || row.student_numbers, true); // Student Number - Number

        // Apply section color to data rows - columns A through L only
        const currentSectionColor = sectionColors[Math.max(0, currentSectionIndex)];

        // Check if this row needs special Excel marking for manual intervention
        const needsManualIntervention = row.needs_excel_marking === true;
        const rowFillColor = needsManualIntervention ? "FFFFFF00" : currentSectionColor; // Yellow for manual intervention

        for (let col = 1; col <= headers.length; col++) {
          const cell = dataRow.getCell(col);

          // FIRST: Set number format BEFORE setting values - BUT be more permissive
          if (col === 1) {
            // Item No - Number format
            cell.numFmt = "0";
          } else if (col === 2) {
            // Unit No - Force Text format to prevent number detection
            cell.numFmt = "@";
          } else if (col === 10) {
            // Room Auto Allocation - Use GENERAL format to prevent data loss
            cell.numFmt = "General";
          } else if (col === 11 || col === 12) {
            // Students Coming and Student Numbers - Number format
            cell.numFmt = "0";
          } else {
            // Text columns - General format
            cell.numFmt = "General";
          }

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: rowFillColor },
          };

          // Special styling for manual intervention rows
          if (needsManualIntervention) {
            cell.font = { size: 11, bold: true };
            cell.border = {
              top: { style: "medium", color: { argb: "FFFF6600" } },
              left: { style: "medium", color: { argb: "FFFF6600" } },
              bottom: { style: "medium", color: { argb: "FFFF6600" } },
              right: { style: "medium", color: { argb: "FFFF6600" } },
            };
          } else {
            cell.font = { size: 11 };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }

          // Center align only columns J, K, L (columns 10, 11, 12)
          const alignment = col >= 10 && col <= 12 ? "center" : "left";
          cell.alignment = { horizontal: alignment, vertical: "middle" };
        }
        currentRow++;
      }
    };

    // Process data in batches
    for (let batchStart = 0; batchStart < data.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, data.length);
      const batch = data.slice(batchStart, batchEnd);

      // Process each row in the current batch
      batch.forEach(processRow);

      // Add delay between batches (except for the last batch)
      if (batchEnd < data.length) {
        await delay(DELAY_MS);
      }
    }

    console.log("All batches processed successfully");

    // Generate Excel file buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Create blob and download
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = originalFileName.replace(/\.[^/.]+$/, "_processed.xlsx");
    saveAs(blob, fileName);

    console.log("Excel file generated successfully with ExcelJS styling");
  } catch (error) {
    throw new Error(`Failed to generate Excel file: ${error.message}`);
  }
};
